import { useSetChatroomStatusMutation } from '@pba/react-chat-core';
import { isGroupChatroom } from 'models';
import { useCallback } from 'react';
import { GroupLineIcon, InfinityLoading, MessagesSidebarItem } from 'ui';
import { useDebounce } from 'common/hooks';
import { images } from 'common/utils';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { useGetNodeUserAvatar } from 'features/users/hooks';
import { ChatSidebarSkeleton } from '../../chat-sidebar-skeleton';
import { useGetChatroomName } from '../../hooks';
import { useChatroomsInfiniteQuery } from '../../queries';
import {
  type ChatroomSidebarContentFilter,
  useGetChatroomLastMessageAuthor,
  useGetChatroomSidebarActions,
} from '../hooks';
import { ChatroomSidebarEmpty } from './chatroom-sidebar-empty';
import { useGetLastMessage } from './hooks';

type P = {
  filter: ChatroomSidebarContentFilter;
  onChatDeleted: () => Promise<void>;
  onChatRoomClicked: (id: string) => Promise<void>;
  onCreateChatClicked: () => void;
  selectedChatroomId?: string;
};

const ChatroomSidebarContent = ({
  filter,
  onChatDeleted,
  onChatRoomClicked,
  onCreateChatClicked,
  selectedChatroomId,
}: P) => {
  const isFilteredView = Object.values(filter).some(Boolean);
  const {
    nodeUserData: { _id },
  } = useUser();
  const { mutate: setChatroomStatus } = useSetChatroomStatusMutation();
  const { isDebouncing, value: search } = useDebounce(filter.search);
  const { contextHolder, getActions, infoModal } = useGetChatroomSidebarActions({
    onChatDeleted,
    selectedChatroomId,
  });
  const getLastMessage = useGetLastMessage();
  const getChatroomLastMessageAuthor = useGetChatroomLastMessageAuthor();
  const getChatroomName = useGetChatroomName();
  const getAvatarUrl = useGetNodeUserAvatar();
  const {
    data: chatrooms,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useChatroomsInfiniteQuery({
    filter: {
      search,
      tags: filter.type === 'groups' ? 'group' : undefined,
      unread: filter.type === 'unread' ? true : undefined,
    },
  });

  const onChatroomClick = useCallback(
    async (id: string) => {
      setChatroomStatus({ chatroomId: id, status: 'read' });
      await onChatRoomClicked(id);
    },
    [onChatRoomClicked, setChatroomStatus],
  );

  if (isDebouncing || isLoading) {
    return <ChatSidebarSkeleton />;
  }

  if (chatrooms?.length === 0 && isFilteredView) {
    return <ChatroomSidebarEmpty filter={filter} onCreateChatClicked={onCreateChatClicked} />;
  }

  return (
    <>
      {contextHolder}
      {infoModal}
      {selectedChatroomId ? null : <MessagesSidebarItem type="new" />}
      {chatrooms?.map((chatroom) => (
        <MessagesSidebarItem
          avatar={
            isGroupChatroom({ chatroom })
              ? {
                  fallbackPhoto: false,
                  icon: <GroupLineIcon size="small" type="dark-default" />,
                }
              : {
                  fallbackPhoto: images.default.userProfilePhoto,
                  src: getAvatarUrl({
                    userId: chatroom.users.find((memberUserId) => memberUserId !== _id) ?? _id,
                  }),
                }
          }
          conversationSummary={{
            actions: { items: getActions(chatroom) },
            timestamp: chatroom.message?.createdAt ?? chatroom.updatedAt,
            title: getChatroomName({
              name: chatroom.name,
              participants: chatroom.participants,
              users: chatroom.users,
            }),
            unread: (chatroom.stats?.unread ?? 0) > 0,
          }}
          key={chatroom.id}
          onClick={async () => onChatroomClick(chatroom.id)}
          preview={{
            lastMessage: {
              author: getChatroomLastMessageAuthor(chatroom),
              message: getLastMessage({ message: chatroom.message }),
            },
          }}
          selected={selectedChatroomId === chatroom.id}
          type="conversation"
        />
      ))}
      <InfinityLoading hasMoreData={hasNextPage} isLoading={isFetching} onEnter={fetchNextPage} />
    </>
  );
};

export default ChatroomSidebarContent;
